import React, { useEffect, useRef, useState } from "react";
import { Mic, X, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api/client";
import { useCart } from "../contexts/CartContext";

/**
 * Voice ordering modal — Web Speech API → Gemini → action proposal.
 * Web-only (Chromium/Safari). Falls back gracefully when STT is unavailable.
 */
const isSttSupported = () =>
    typeof window !== "undefined" && ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);

const VoiceModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { refresh: refreshCart } = useCart();
    const [supported] = useState(isSttSupported());
    const [listening, setListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [interp, setInterp] = useState(null);
    const [interpreting, setInterpreting] = useState(false);
    const [executing, setExecuting] = useState(false);
    const recogRef = useRef(null);

    const stop = () => {
        try { recogRef.current?.stop(); } catch { /* noop */ }
        setListening(false);
    };

    useEffect(() => {
        if (!open) {
            stop();
            setTranscript("");
            setInterp(null);
        }
    }, [open]);

    const startListening = () => {
        if (!supported) {
            toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
            return;
        }
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recog = new SR();
        recog.lang = "en-IN";
        recog.continuous = false;
        recog.interimResults = true;
        recog.maxAlternatives = 1;

        let finalText = "";
        recog.onresult = (e) => {
            let interim = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const txt = e.results[i][0].transcript;
                if (e.results[i].isFinal) finalText += txt;
                else interim += txt;
            }
            setTranscript((finalText + " " + interim).trim());
        };
        recog.onend = () => {
            setListening(false);
            if (finalText.trim()) {
                interpret(finalText.trim());
            }
        };
        recog.onerror = (e) => {
            console.warn("SR error", e);
            setListening(false);
            if (e.error === "not-allowed") {
                toast.error("Microphone access denied. Enable mic permission and retry.");
            }
        };

        try {
            recog.start();
            setListening(true);
            setTranscript("");
            setInterp(null);
            recogRef.current = recog;
        } catch (err) {
            console.warn(err);
        }
    };

    const interpret = async (text) => {
        try {
            setInterpreting(true);
            const { data } = await api.post("/voice/interpret", { transcript: text });
            setInterp(data);
        } catch (err) {
            toast.error("Could not interpret. Please try again.");
            setInterp({
                intent: "unknown",
                clarification_needed: true,
                clarification_question: "Something went wrong. Could you try once more?",
            });
        } finally {
            setInterpreting(false);
        }
    };

    const executeAction = async () => {
        const action = interp?.suggested_action;
        if (!action) return;
        try {
            setExecuting(true);
            if (action.method === "POST") {
                await api.post(action.endpoint.replace(/^\/api/, ""), action.payload);
                toast.success(action.label);
                refreshCart?.();
                onClose?.();
            } else if (action.method === "NAV") {
                // Strip the leading /api if anyone added it
                navigate(action.endpoint);
                onClose?.();
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not complete action");
        } finally {
            setExecuting(false);
        }
    };

    const tryManualInterpret = () => {
        const t = transcript.trim();
        if (t) interpret(t);
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/50 backdrop-blur-sm p-0 sm:items-center sm:p-4"
            onClick={onClose}
            data-testid="voice-modal"
        >
            <div
                className="relative w-full max-w-lg overflow-hidden rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"
                >
                    <X className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-brand-600" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">Voice ordering</p>
                </div>
                <h3 className="mt-1 text-xl font-semibold text-zinc-900">What can I help with?</h3>
                <p className="mt-1 text-sm text-zinc-500">
                    Try: <span className="italic">"Add two strips of paracetamol"</span> or <span className="italic">"Show me my orders"</span>.
                </p>

                <div className="my-6 flex flex-col items-center">
                    <button
                        onClick={listening ? stop : startListening}
                        disabled={!supported}
                        className={`relative inline-flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg transition-all ${
                            listening ? "bg-rose-600 animate-pulse" : "bg-brand-700 hover:bg-brand-800"
                        } disabled:cursor-not-allowed disabled:opacity-40`}
                        data-testid="voice-mic-btn"
                    >
                        <Mic className="h-8 w-8" />
                        {listening && (
                            <span className="absolute inset-0 rounded-full ring-4 ring-rose-300/60" />
                        )}
                    </button>
                    <p className="mt-3 text-xs text-zinc-500" data-testid="voice-mic-status">
                        {!supported
                            ? "Voice not supported in this browser."
                            : listening
                            ? "Listening… tap to stop"
                            : transcript
                            ? "Tap to record again"
                            : "Tap the mic to speak"}
                    </p>
                </div>

                {transcript && (
                    <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3" data-testid="voice-transcript">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">You said</p>
                        <p className="mt-1 text-sm text-zinc-800">{transcript}</p>
                        {!interpreting && !interp && (
                            <button
                                onClick={tryManualInterpret}
                                className="mt-2 text-xs font-medium text-brand-700 hover:underline"
                                data-testid="voice-interpret-btn"
                            >
                                Interpret again
                            </button>
                        )}
                    </div>
                )}

                {interpreting && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-zinc-600" data-testid="voice-interpreting">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Interpreting your request…
                    </div>
                )}

                {interp && (
                    <div className="mt-4" data-testid="voice-interpretation">
                        {interp.clarification_needed && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="voice-clarification">
                                <p className="font-medium">Could you clarify?</p>
                                <p className="mt-1 text-amber-800">{interp.clarification_question || "I didn't catch a specific product."}</p>
                            </div>
                        )}
                        {interp.suggested_action && (
                            <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-3" data-testid="voice-suggested-action">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">Suggested action</p>
                                <p className="mt-1 text-sm font-medium text-brand-900">{interp.suggested_action.label}</p>
                                <button
                                    onClick={executeAction}
                                    disabled={executing}
                                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                                    data-testid="voice-execute-btn"
                                >
                                    {executing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                                    {executing ? "Doing…" : "Confirm"}
                                </button>
                            </div>
                        )}
                        {interp.intent === "unknown" && !interp.clarification_needed && (
                            <p className="text-sm text-zinc-500">I'm not sure I can help with that yet. Try asking about a product or your orders.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceModal;
